import { ApolloClient, NormalizedCacheObject, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import { ProductGroupColorSelector } from '../../../../../__generated__/graphql/catalog/graphql'
import { ProductPageQueryQueryVariables } from '../../../../../__generated__/graphql/catalog/graphql'
import { useHeap } from '../../../../../app/_components/chrome/scripts/HeapScript'
import { usePortal } from '../../../../../app/_providers/PortalProvider.client'
import { usePagesDirectoryStableQueryParams } from '../../../../../lib/adapters/next-routing-service/useUpdateQueryParam'
import { GET_PDP_FEATURED_PRODUCT, PRODUCT_PAGE_QUERY } from '../../../../../lib/graphql/queries'
import { QueryStatus } from '../../../../../lib/hooks/types'

import { ProductPageProduct } from './types/ProductPageProduct'
import { ProductPageProductGroup } from './types/ProductPageProductGroup'
import { useProductPagePortal } from './useProductPagePortal'

/**
 * @deprecated Use your own query for what you need for your case. Or use usePdpServerSideProps if on the PDP and it has what you need.
 */
export type UseFetchProductPageDataProps = {
  productGroupHandle: string
  includeUnavailable?: boolean
  isPreview?: boolean
  featuredProductColor?: string
  featuredProductFit?: string
  launchTags?: string
  customerTier?: string
  collectionHandle?: string
  collectionScope?: ProductGroupColorSelector
  /** Whether or not portal-specific variables should be included in the product page queries */
  withPortalContext?: boolean
  /** Whether or not B2B-specific logos should be returned for the featured product. */
  withB2BLogos?: boolean
}

/**
 * @deprecated Use your own query for what you need for your case. Or use usePdpServerSideProps if on the PDP and it has what you need.
 */
export type UseFetchProductPageDataOutput = {
  featuredProduct: ProductPageProduct | undefined
  featuredProductStatus: QueryStatus
  featuredProductNotFound: boolean
  featuredProductError: Error | undefined
  productGroup: ProductPageProductGroup | undefined
  productGroupStatus: QueryStatus
  productGroupNotFound: boolean
  productGroupError: Error | undefined
}

/**
 * @deprecated Use your own query for what you need for your case. Or use usePdpServerSideProps if on the PDP and it has what you need.
 */
export type ProductPageProductGroupData = {
  productGroup?: ProductPageProductGroup
}

/**
 * @deprecated Use your own query for what you need for your case. Or use usePdpServerSideProps if on the PDP and it has what you need.
 */
export type ProductPageFeaturedProductData = {
  productGroup?: {
    featuredProduct?: ProductPageProduct
  }
}

/**
 * @deprecated Use your own query for what you need for your case. Or use usePdpServerSideProps if on the PDP and it has what you need.
 */
export const primeClientForFetchProductPageData = ({
  client,
  props,
}: {
  client: ApolloClient<NormalizedCacheObject>
  props: UseFetchProductPageDataProps
}) => {
  const {
    productGroupHandle,
    includeUnavailable,
    isPreview,
    featuredProductColor,
    featuredProductFit,
    launchTags,
    customerTier,
    collectionHandle,
    collectionScope = 'ALL',
  } = props

  return [
    client.query<ProductPageProductGroupData, ProductPageQueryQueryVariables>({
      errorPolicy: 'all',
      query: PRODUCT_PAGE_QUERY,
      variables: {
        handle: productGroupHandle,
        collectionHandle,
        collectionScope,
        /* Existence of portal is only determined on the client */
        withPortalColors: false,
      },
      context: { includeUnavailable, isPreview, launchTags, customerTier },
    }),
    client.query<ProductPageFeaturedProductData>({
      errorPolicy: 'all',
      query: GET_PDP_FEATURED_PRODUCT,
      variables: {
        productGroupHandle,
        color: featuredProductColor,
        fit: featuredProductFit,
        collectionHandle,
        logoScope: 'DTC',
      },
      context: { includeUnavailable, isPreview, launchTags, customerTier },
    }),
  ] as const
}

/**
 * @deprecated Use your own query for what you need for your case. Or use usePdpServerSideProps if on the PDP and it has what you need.
 */
export function useFetchProductPageData({
  productGroupHandle,
  collectionHandle,
  collectionScope = 'ALL',
  includeUnavailable,
  isPreview,
  featuredProductColor,
  featuredProductFit,
  launchTags,
  customerTier,
  withPortalContext = true,
  withB2BLogos = false,
}: UseFetchProductPageDataProps): UseFetchProductPageDataOutput {
  const { portal } = usePortal()

  const queryContext = useMemo(() => {
    return { includeUnavailable, isPreview, launchTags, customerTier }
  }, [customerTier, includeUnavailable, isPreview, launchTags])

  // Whether the `portalColorGroups` field should be requested in the product group query.
  const withPortalColors = Boolean(withPortalContext && portal?.collectionHandle)

  /**
   * ProductGroup Query
   */
  const {
    data: productGroupData,
    error: productGroupError,
    loading: productGroupLoading,
    previousData: previousProductGroupData,
    called: productGroupCalled,
  } = useQuery<ProductPageProductGroupData, ProductPageQueryQueryVariables>(PRODUCT_PAGE_QUERY, {
    variables: {
      handle: productGroupHandle,
      collectionHandle,
      collectionScope,
      /* Portal-specific arguments */
      portalCollectionHandle: portal?.collectionHandle,
      withPortalColors,
    },
    context: queryContext,
  })
  const productGroup = productGroupLoading
    ? previousProductGroupData?.productGroup
    : productGroupData?.productGroup
  const productGroupStatus = useMemo<QueryStatus>(() => {
    if (productGroupLoading) return 'pending'
    if (productGroupError) 'rejected'
    if (productGroupCalled || productGroup) return 'resolved'
    return 'idle'
  }, [productGroup, productGroupCalled, productGroupError, productGroupLoading])

  const productGroupNotFound: boolean = productGroup === null && productGroupStatus === 'resolved'

  const { isPortalColorSelected } = useProductPagePortal({
    portalColorGroups: productGroup?.portalColorGroups ?? [],
    selectedColorKey: featuredProductColor,
  })
  const { sendHeapEvent } = useHeap()
  const {
    query: { color: colorQueryParam },
  } = usePagesDirectoryStableQueryParams()
  /**
   * FeaturedProduct Query (the color fit combo for this ProductGroup that is selected to view)
   */
  const {
    error: featuredProductError,
    data: featuredProductData,
    loading: featuredProductLoading,
    previousData: previousFeaturedProductData,
    called: featuredProductCalled,
  } = useQuery<ProductPageFeaturedProductData>(GET_PDP_FEATURED_PRODUCT, {
    variables: {
      productGroupHandle,
      color: featuredProductColor,
      fit: featuredProductFit,
      collectionHandle,
      logoScope: withB2BLogos || isPortalColorSelected ? 'PORTALS' : 'DTC',
    },
    context: queryContext,
    onCompleted: data => {
      const sizes = data.productGroup?.featuredProduct?.variants?.map(variant => {
        return { shorthand: variant.size, soldOut: variant.soldOut }
      })
      if (sizes && sizes.length > 0) {
        sendHeapEvent({
          eventName: 'pdp-size-availiability',
          props: {
            'size-availability': sizes
              .map(size => `${size.shorthand}:${size.soldOut ? 'unavailable' : 'available'}`)
              .toString(),
            'color': colorQueryParam?.toString() ?? 'unknown',
          },
        })
      }
    },
  })
  const featuredProduct = featuredProductLoading
    ? previousFeaturedProductData?.productGroup?.featuredProduct
    : featuredProductData?.productGroup?.featuredProduct
  const featuredProductStatus = useMemo<QueryStatus>(() => {
    if (featuredProductLoading) return 'pending'
    if (featuredProductError) 'rejected'
    if (featuredProductCalled || featuredProduct) return 'resolved'
    return 'idle'
  }, [featuredProductCalled, featuredProductError, featuredProductLoading, featuredProduct])

  const featuredProductNotFound: boolean =
    featuredProduct === null && featuredProductStatus === 'resolved'

  return {
    featuredProduct,
    featuredProductStatus,
    featuredProductNotFound,
    featuredProductError,
    productGroup,
    productGroupStatus,
    productGroupNotFound,
    productGroupError,
  }
}
